/**
 * Random-access ZIP reader built on Blob.slice + DecompressionStream.
 *
 * A dropped .zip arrives as a File, which gives us random access without ever
 * materialising the archive in memory. So instead of scanning local file
 * headers front-to-back (which forces you to hold the stream), we read the
 * End Of Central Directory record from the tail, parse the central directory,
 * and then slice each entry's bytes on demand. A 4GB CD image costs us the
 * central directory (a few hundred KB) plus one entry at a time.
 *
 * Supported: stored (0) and deflate (8), plus ZIP64 offsets/sizes.
 * Not supported: encryption, and the exotic compression methods (bzip2, LZMA,
 * zstd) that no real-world game archive uses.
 */

const EOCD_SIG = 0x06054b50;
const EOCD64_SIG = 0x06064b50;
const EOCD64_LOC_SIG = 0x07064b50;
const CDFH_SIG = 0x02014b50;

export interface ZipEntry {
  name: string;
  compressedSize: number;
  uncompressedSize: number;
  method: number;
  crc32: number;
  localHeaderOffset: number;
  isDirectory: boolean;
  lastModified: number;
}

export class ZipReadError extends Error {}

const CRC32_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = (c & 1) !== 0 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (const byte of bytes) c = CRC32_TABLE[(c ^ byte) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

function dv(buf: ArrayBuffer) {
  return new DataView(buf);
}

async function slice(file: Blob, start: number, end: number): Promise<ArrayBuffer> {
  return file.slice(start, end).arrayBuffer();
}

function dosDateToMs(time: number, date: number): number {
  const year = ((date >> 9) & 0x7f) + 1980;
  const month = ((date >> 5) & 0x0f) - 1;
  const day = date & 0x1f;
  const hours = (time >> 11) & 0x1f;
  const mins = (time >> 5) & 0x3f;
  const secs = (time & 0x1f) * 2;
  return new Date(year, month, day, hours, mins, secs).getTime();
}

export async function readCentralDirectory(file: Blob): Promise<ZipEntry[]> {
  const tailLen = Math.min(file.size, 0x10000 + 22);
  const tail = await slice(file, file.size - tailLen, file.size);
  const t = dv(tail);

  let eocd = -1;
  for (let i = tail.byteLength - 22; i >= 0; i--) {
    if (t.getUint32(i, true) === EOCD_SIG) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new ZipReadError('Not a zip file (no end-of-central-directory record).');

  let entryCount = t.getUint16(eocd + 10, true);
  let cdSize = t.getUint32(eocd + 12, true);
  let cdOffset = t.getUint32(eocd + 16, true);
  if (t.getUint16(eocd + 4, true) !== 0 || t.getUint16(eocd + 6, true) !== 0) {
    throw new ZipReadError('Multi-disk zip archives are not supported.');
  }

  // ZIP64: the 32-bit fields are saturated and the real values live in the
  // ZIP64 EOCD record pointed at by the locator that precedes the EOCD.
  if (cdOffset === 0xffffffff || entryCount === 0xffff || cdSize === 0xffffffff) {
    const locOff = eocd - 20;
    if (locOff >= 0 && t.getUint32(locOff, true) === EOCD64_LOC_SIG) {
      const eocd64Off = Number(t.getBigUint64(locOff + 8, true));
      if (!Number.isSafeInteger(eocd64Off) || eocd64Off < 0 || eocd64Off + 56 > file.size) {
        throw new ZipReadError('ZIP64 directory record points outside the zip file.');
      }
      const rec = dv(await slice(file, eocd64Off, eocd64Off + 56));
      if (rec.getUint32(0, true) !== EOCD64_SIG) throw new ZipReadError('Malformed ZIP64 record.');
      entryCount = Number(rec.getBigUint64(32, true));
      cdSize = Number(rec.getBigUint64(40, true));
      cdOffset = Number(rec.getBigUint64(48, true));
    }
  }
  if (!Number.isSafeInteger(entryCount) || entryCount < 1 || entryCount > 100_000) {
    throw new ZipReadError(`Unreasonable zip entry count: ${entryCount}.`);
  }

  if (!Number.isSafeInteger(cdOffset) || !Number.isSafeInteger(cdSize)
      || cdOffset < 0 || cdSize < 0 || cdOffset + cdSize > file.size) {
    throw new ZipReadError('Central directory points outside the zip file.');
  }
  const cd = dv(await slice(file, cdOffset, cdOffset + cdSize));
  const dec = new TextDecoder('utf-8');
  const entries: ZipEntry[] = [];
  let p = 0;
  for (let i = 0; i < entryCount; i++) {
    if (p + 46 > cd.byteLength || cd.getUint32(p, true) !== CDFH_SIG) {
      throw new ZipReadError(`Malformed central directory at entry ${i + 1} of ${entryCount}.`);
    }
    const flags = cd.getUint16(p + 8, true);
    const method = cd.getUint16(p + 10, true);
    const modTime = cd.getUint16(p + 12, true);
    const modDate = cd.getUint16(p + 14, true);
    const crc32 = cd.getUint32(p + 16, true);
    let csize = cd.getUint32(p + 20, true);
    let usize = cd.getUint32(p + 24, true);
    const nameLen = cd.getUint16(p + 28, true);
    const extraLen = cd.getUint16(p + 30, true);
    const commentLen = cd.getUint16(p + 32, true);
    let lho = cd.getUint32(p + 42, true);
    const next = p + 46 + nameLen + extraLen + commentLen;
    if (next > cd.byteLength) throw new ZipReadError(`Truncated central-directory entry ${i + 1}.`);

    const nameBytes = new Uint8Array(cd.buffer, cd.byteOffset + p + 46, nameLen);
    const name = dec.decode(nameBytes).replace(/\\/g, '/');

    if (csize === 0xffffffff || usize === 0xffffffff || lho === 0xffffffff) {
      let e = p + 46 + nameLen;
      const extraEnd = e + extraLen;
      while (e + 4 <= extraEnd) {
        const id = cd.getUint16(e, true);
        const sz = cd.getUint16(e + 2, true);
        if (id === 0x0001) {
          let q = e + 4;
          const take = (): number => {
            if (q + 8 > e + 4 + sz || q + 8 > extraEnd) throw new ZipReadError(`Truncated ZIP64 data for ${name}.`);
            const value = Number(cd.getBigUint64(q, true));
            q += 8;
            return value;
          };
          if (usize === 0xffffffff) usize = take();
          if (csize === 0xffffffff) csize = take();
          if (lho === 0xffffffff) lho = take();
          break;
        }
        e += 4 + sz;
      }
    }
    if (flags & 0x1) throw new ZipReadError(`Encrypted zip entries are not supported (${name}).`);

    entries.push({
      name,
      method,
      crc32,
      compressedSize: csize,
      uncompressedSize: usize,
      localHeaderOffset: lho,
      isDirectory: name.endsWith('/'),
      lastModified: dosDateToMs(modTime, modDate),
    });
    p = next;
  }
  if (entries.length !== entryCount || p !== cd.byteLength) {
    throw new ZipReadError('Zip central-directory length does not match its entry table.');
  }
  if (!entries.length) throw new ZipReadError('Zip central directory is empty.');
  return entries;
}

/** Byte range of an entry's payload, resolved by reading its local header. */
async function payloadRange(file: Blob, entry: ZipEntry): Promise<[number, number]> {
  if (!Number.isSafeInteger(entry.localHeaderOffset) || entry.localHeaderOffset < 0
      || entry.localHeaderOffset + 30 > file.size) throw new ZipReadError(`Local header is out of range for ${entry.name}.`);
  const h = dv(await slice(file, entry.localHeaderOffset, entry.localHeaderOffset + 30));
  if (h.getUint32(0, true) !== 0x04034b50) throw new ZipReadError(`Bad local header for ${entry.name}.`);
  const nameLen = h.getUint16(26, true);
  const extraLen = h.getUint16(28, true);
  const start = entry.localHeaderOffset + 30 + nameLen + extraLen;
  if (!Number.isSafeInteger(entry.compressedSize) || entry.compressedSize < 0
      || start < 0 || start + entry.compressedSize > file.size) {
    throw new ZipReadError(`Compressed payload is out of range for ${entry.name}.`);
  }
  return [start, start + entry.compressedSize];
}

/** A ReadableStream of the entry's decompressed bytes. Never buffers the whole entry. */
export async function entryStream(file: Blob, entry: ZipEntry): Promise<ReadableStream<Uint8Array>> {
  const [start, end] = await payloadRange(file, entry);
  const raw = file.slice(start, end).stream() as ReadableStream<Uint8Array>;
  if (entry.method === 0) return raw;
  if (entry.method === 8) {
    if (typeof DecompressionStream === 'undefined')
      throw new ZipReadError('This browser cannot inflate deflate-compressed zips (no DecompressionStream).');
    // TS models DecompressionStream's writable side as BufferSource, which does
    // not unify with ReadableStream<Uint8Array>; the runtime contract is fine.
    const ds = new DecompressionStream('deflate-raw') as unknown as ReadableWritablePair<Uint8Array, Uint8Array>;
    return raw.pipeThrough(ds);
  }
  throw new ZipReadError(`Unsupported compression method ${entry.method} for ${entry.name}.`);
}

/**
 * Entry contents as a Blob. Built by collecting stream chunks — the peak cost
 * is one entry, not the archive. Stored entries skip the copy entirely.
 */
export async function entryBlob(file: Blob, entry: ZipEntry): Promise<Blob> {
  const [start, end] = await payloadRange(file, entry);
  if (entry.method === 0) return file.slice(start, end);
  const stream = await entryStream(file, entry);
  const parts: BlobPart[] = [];
  const reader = stream.getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) parts.push(new Uint8Array(value));
  }
  return new Blob(parts);
}

/** Inflate one entry and verify the metadata that protects imports from truncation and corruption. */
export async function verifiedEntryBytes(file: Blob, entry: ZipEntry, maxBytes: number): Promise<Uint8Array> {
  if (!Number.isSafeInteger(entry.uncompressedSize) || entry.uncompressedSize < 0) {
    throw new ZipReadError(`Invalid uncompressed size for ${entry.name}.`);
  }
  if (entry.uncompressedSize > maxBytes) {
    throw new ZipReadError(`${entry.name} expands to ${entry.uncompressedSize} bytes (limit ${maxBytes}).`);
  }
  const stream = await entryStream(file, entry);
  const reader = stream.getReader();
  const parts: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (!value) continue;
    total += value.byteLength;
    if (total > maxBytes || total > entry.uncompressedSize) {
      await reader.cancel();
      throw new ZipReadError(`${entry.name} expanded beyond its declared or allowed size.`);
    }
    parts.push(Uint8Array.from(value));
  }
  if (total !== entry.uncompressedSize) {
    throw new ZipReadError(`${entry.name} expanded to ${total} bytes, expected ${entry.uncompressedSize}.`);
  }
  const bytes = new Uint8Array(total);
  let at = 0;
  for (const part of parts) { bytes.set(part, at); at += part.length; }
  if (crc32(bytes) !== entry.crc32) throw new ZipReadError(`CRC mismatch for ${entry.name}.`);
  return bytes;
}
